import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { CONTROL } from "@/constants/testIds";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleLeft, ToggleRight, Plus, Trash2, Save, Clock } from "lucide-react";
import { toast } from "sonner";

function ManualPanel({ cfg, onUpdate, disabled, batchCount = 0 }) {
  const [timeout_, setTimeout_] = useState(cfg.timeout_minutes);
  const willAffectBatch = batchCount >= 2;
  return (
    <div className="space-y-6">
      {willAffectBatch && (
        <div
          className="text-xs px-3 py-2 rounded-md bg-primary/10 border border-primary/30 text-primary"
          data-testid="manual-batch-hint"
        >
          <strong>{batchCount} valve terpilih</strong> — toggle di bawah akan mempengaruhi semua valve yang tercentang.
        </div>
      )}
      <div className="flex items-center justify-between p-4 border border-border rounded-md">
        <div>
          <div className="font-medium">Solenoid Valve</div>
          <div className="text-xs text-muted-foreground">
            {cfg.is_on ? `Aktif sejak ${new Date(cfg.on_since).toLocaleTimeString()}` : "Mati"}
          </div>
        </div>
        <Switch
          checked={cfg.is_on}
          disabled={disabled}
          onCheckedChange={(v) => onUpdate({ is_on: v })}
          data-testid={CONTROL.manualToggle}
        />
      </div>
      <div className="space-y-2 max-w-xs">
        <Label htmlFor="timeout">Batas waktu penyalaan (menit)</Label>
        <div className="flex gap-2">
          <Input
            id="timeout"
            type="number"
            min={1}
            max={120}
            value={timeout_}
            onChange={(e) => setTimeout_(Number(e.target.value))}
            data-testid={CONTROL.timeoutInput}
          />
          <Button
            type="button"
            onClick={() => onUpdate({ timeout_minutes: timeout_ })}
            disabled={disabled}
            data-testid={CONTROL.saveButton}
          >
            <Save className="w-4 h-4 mr-1" /> Simpan
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Default 15 menit. Solenoid akan mati otomatis setelah durasi ini tercapai.</p>
      </div>
      <div className="flex items-start justify-between gap-4 p-4 border border-dashed border-border rounded-md">
        <div className="flex-1">
          <div className="font-medium text-sm">Wiring terbalik (invert output)</div>
          <div className="text-xs text-muted-foreground mt-1">
            Aktifkan jika kondisi valve fisik berlawanan dengan status UI (mis. UI "ON" tapi valve mati). Backend akan membalik sinyal yang dikirim ke ESP32 untuk valve ini saja — tidak perlu re-flash firmware.
          </div>
        </div>
        <Switch
          checked={!!cfg.invert_output}
          disabled={disabled}
          onCheckedChange={(v) => onUpdate({ invert_output: v })}
          data-testid="manual-invert-toggle"
        />
      </div>
    </div>
  );
}

function SchedulePanel({ cfg, onUpdate, disabled }) {
  const [schedules, setSchedules] = useState(cfg.schedules?.length ? cfg.schedules : []);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync local state when cfg.schedules changes from server (poll refresh) — but only
  // if user has no unsaved changes, so their in-progress edits aren't clobbered.
  useEffect(() => {
    if (dirty) return;
    setSchedules(cfg.schedules?.length ? cfg.schedules : []);
  }, [cfg.schedules, dirty]);

  const addSchedule = () => {
    setDirty(true);
    setSchedules([
      ...schedules,
      {
        label: schedules.length === 0 ? "Pagi" : "Sore",
        start_time: schedules.length === 0 ? "06:00" : "16:00",
        duration_seconds: 60,
        recurrence_value: 1,
        recurrence_unit: "day",
        ends: "never",
        end_date: null,
        end_time: null,
      },
    ]);
  };

  const removeSchedule = (i) => {
    setDirty(true);
    setSchedules(schedules.filter((_, idx) => idx !== i));
  };
  const updateField = (i, key, value) => {
    setDirty(true);
    const next = [...schedules];
    next[i] = { ...next[i], [key]: value };
    setSchedules(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      // Ensure mode is 'schedule' when saving (guard against race where mode wasn't
      // persisted from tab switch — makes the button self-sufficient).
      await onUpdate({ mode: "schedule", schedules });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Atur jadwal pagi & sore dengan durasi, pengulangan, dan end of occurrence.
        </p>
        <Button variant="outline" size="sm" onClick={addSchedule} data-testid={CONTROL.scheduleAddButton} disabled={disabled}>
          <Plus className="w-4 h-4 mr-1" /> Tambah jadwal
        </Button>
      </div>
      {schedules.length === 0 && (
        <div className="border border-dashed border-border rounded-md p-8 text-center text-muted-foreground text-sm">
          Belum ada jadwal. Tambahkan jadwal pagi/sore.
        </div>
      )}
      {schedules.map((sch, i) => {
        // Compute "next fire" in WIB (server timezone) so user can visually confirm
        // the schedule works BEFORE waiting for real ESP32 physical action.
        const nowUtc = new Date();
        const nowWib = new Date(nowUtc.getTime() + 7 * 3600 * 1000);
        const [sh, sm] = (sch.start_time || "06:00").split(":").map(Number);
        const dur = Number(sch.duration_seconds) || 60;
        const todayStart = new Date(Date.UTC(nowWib.getUTCFullYear(), nowWib.getUTCMonth(), nowWib.getUTCDate(), sh, sm, 0));
        const yesterdayStart = new Date(todayStart.getTime() - 24 * 3600 * 1000);
        const tomorrowStart = new Date(todayStart.getTime() + 24 * 3600 * 1000);
        const nowWibMs = nowWib.getTime();
        let windowLabel = null;
        for (const startMoment of [todayStart, yesterdayStart]) {
          const delta = (nowWibMs - startMoment.getTime()) / 1000;
          if (delta >= 0 && delta < dur) {
            const remain = Math.round(dur - delta);
            windowLabel = { active: true, text: `Sedang aktif · ${remain}s tersisa` };
            break;
          }
        }
        if (!windowLabel) {
          // find next start
          let nextStart = todayStart.getTime() > nowWibMs ? todayStart : tomorrowStart;
          const untilS = Math.round((nextStart.getTime() - nowWibMs) / 1000);
          const hrs = Math.floor(untilS / 3600);
          const mins = Math.floor((untilS % 3600) / 60);
          windowLabel = {
            active: false,
            text: `Aktif berikutnya dalam ${hrs > 0 ? `${hrs} jam ` : ""}${mins} menit`,
          };
        }
        return (
        <div key={i} className="border border-border rounded-md p-4 space-y-4" data-testid={`schedule-entry-${i}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              <Input
                value={sch.label}
                onChange={(e) => updateField(i, "label", e.target.value)}
                className="w-32 h-8 text-sm"
                placeholder="Label"
              />
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeSchedule(i)} disabled={disabled}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Jam mulai</Label>
              <Input type="time" value={sch.start_time} onChange={(e) => updateField(i, "start_time", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Durasi (detik)</Label>
              <Input type="number" min={1} value={sch.duration_seconds} onChange={(e) => updateField(i, "duration_seconds", Number(e.target.value))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Setiap</Label>
              <Input type="number" min={1} value={sch.recurrence_value} onChange={(e) => updateField(i, "recurrence_value", Number(e.target.value))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Satuan</Label>
              <Select value={sch.recurrence_unit} onValueChange={(v) => updateField(i, "recurrence_unit", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hour">Jam</SelectItem>
                  <SelectItem value="day">Hari</SelectItem>
                  <SelectItem value="week">Minggu</SelectItem>
                  <SelectItem value="month">Bulan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">End of occurrence</Label>
              <Select value={sch.ends} onValueChange={(v) => updateField(i, "ends", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="on">On (tanggal & jam)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sch.ends === "on" && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Tanggal akhir</Label>
                  <Input type="date" value={sch.end_date || ""} onChange={(e) => updateField(i, "end_date", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Jam akhir</Label>
                  <Input type="time" value={sch.end_time || ""} onChange={(e) => updateField(i, "end_time", e.target.value)} />
                </div>
              </>
            )}
          </div>
          {/* Next-fire indicator — helps user confirm schedule interpretation */}
          <div
            className={`text-xs px-2.5 py-1.5 rounded-md ${
              windowLabel.active
                ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
                : "bg-muted/50 border border-border text-muted-foreground"
            }`}
            data-testid={`schedule-next-fire-${i}`}
          >
            {windowLabel.active ? "🟢 " : "⏳ "}{windowLabel.text}
          </div>
        </div>
        );
      })}
      <Button onClick={save} disabled={disabled || saving} data-testid="schedule-save-btn">
        <Save className="w-4 h-4 mr-1" /> {saving ? "Menyimpan…" : "Simpan jadwal"}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        Backend evaluasi jadwal setiap 5 detik dalam zona <strong>WIB (UTC+7)</strong>. Valve akan otomatis ON saat masuk window & OFF saat lewat durasi.
      </p>
    </div>
  );
}

function AutoPanel({ cfg, onUpdate, disabled, soilSensors = [], projectId }) {
  const [range, setRange] = useState([cfg.auto_soil_min_pct, cfg.auto_soil_max_pct]);
  const [refSensorId, setRefSensorId] = useState(cfg.auto_sensor_id || "");
  const [liveValues, setLiveValues] = useState({}); // sensor_id → latest %
  const [liveAvg, setLiveAvg] = useState(null);
  const [liveTs, setLiveTs] = useState(null);
  const [activeCount, setActiveCount] = useState(0); // sensors with fresh reading (last 60s)

  // Sync when cfg refreshes from server so user edits stay in sync
  useEffect(() => {
    setRange([cfg.auto_soil_min_pct, cfg.auto_soil_max_pct]);
    setRefSensorId(cfg.auto_sensor_id || "");
  }, [cfg.id, cfg.auto_soil_min_pct, cfg.auto_soil_max_pct, cfg.auto_sensor_id]);

  // Poll latest reference reading (avg of all OR specific sensor) so user can see it live.
  // "Aktif" = sensor that pushed data within last 60 seconds.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const FRESHNESS_MS = 60 * 1000;
    const fetchLive = async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/latest`);
        if (cancelled) return;
        const now = Date.now();
        // Dedupe by sensor_id (defensive; backend already dedupes) + freshness gate
        const bySensor = new Map();
        for (const r of data) {
          if (r.sensor_type !== "soil_moisture") continue;
          if (!r.timestamp || !r.sensor_id) continue;
          const ageMs = now - new Date(r.timestamp).getTime();
          if (ageMs > FRESHNESS_MS) continue; // skip stale sensor
          bySensor.set(r.sensor_id, r);
        }
        const fresh = Array.from(bySensor.values());
        const perSensor = Object.fromEntries(fresh.map((r) => [r.sensor_id, r.value]));
        setLiveValues(perSensor);
        setActiveCount(fresh.length);
        if (fresh.length) {
          // Explicit "sum of active values / count of active sensors"
          const total = fresh.reduce((sum, r) => sum + r.value, 0);
          setLiveAvg(total / fresh.length);
          setLiveTs(fresh.reduce((max, r) => (r.timestamp > max ? r.timestamp : max), fresh[0].timestamp));
        } else {
          setLiveAvg(null);
          setLiveTs(null);
        }
      } catch { /* silent */ }
    };
    fetchLive();
    const id = setInterval(fetchLive, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [projectId]);

  // Reference value that will drive backend decision — matches simulator logic
  const refValue = refSensorId ? liveValues[refSensorId] : liveAvg;
  const refLabel = refSensorId
    ? (soilSensors.find((s) => s.id === refSensorId)?.name || "Sensor")
    : `Rata-rata ${activeCount} dari ${soilSensors.length} sensor aktif`;
  const inZone =
    refValue == null ? "unknown" :
    refValue < range[0] ? "below-min" :
    refValue > range[1] ? "above-max" : "in-range";

  return (
    <div className="space-y-6 max-w-xl">
      <p className="text-sm text-muted-foreground">
        Solenoid akan menyala saat kelembapan tanah acuan di bawah batas minimum, dan mati saat melebihi batas maksimum.
      </p>

      {/* Live reference reading — helps user tune MIN/MAX intelligently */}
      <div
        className="border border-primary/30 bg-primary/5 rounded-md p-3"
        data-testid="auto-live-ref"
      >
        <div className="flex items-center justify-between gap-3 mb-1">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Nilai acuan saat ini</span>
          {liveTs && (
            <span className="text-[10px] text-muted-foreground">
              {new Date(liveTs).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl font-bold text-primary" data-testid="auto-ref-value">
            {refValue == null ? "—" : refValue.toFixed(1)}
          </span>
          <span className="text-sm text-muted-foreground">%</span>
          <span className="text-xs text-muted-foreground ml-2">({refLabel})</span>
        </div>
        <div className="text-[11px] mt-1.5">
          {inZone === "below-min" && (
            <span className="text-emerald-600 dark:text-emerald-400">↑ Di bawah MIN {range[0]}% → valve akan ON</span>
          )}
          {inZone === "above-max" && (
            <span className="text-blue-600 dark:text-blue-400">↓ Di atas MAX {range[1]}% → valve akan OFF</span>
          )}
          {inZone === "in-range" && (
            <span className="text-muted-foreground">Dalam rentang {range[0]}–{range[1]}% → valve tidak berubah (hysteresis)</span>
          )}
          {inZone === "unknown" && (
            <span className="text-muted-foreground">Belum ada pembacaan — pastikan ESP32 push data soil_moisture.</span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm">Sensor kelembapan acuan</Label>
        <select
          value={refSensorId}
          onChange={(e) => setRefSensorId(e.target.value)}
          disabled={disabled}
          data-testid="auto-sensor-ref"
          className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
        >
          <option value="">Rata-rata semua sensor soil_moisture ({soilSensors.length})</option>
          {soilSensors.map((s) => (
            <option key={s.id} value={s.id}>{s.name} {s.pin ? `· ${s.pin}` : ""}</option>
          ))}
        </select>
        <p className="text-[11px] text-muted-foreground">
          {refSensorId
            ? "Sensor spesifik dipilih — kontrol berdasarkan pembacaan tunggal (paling responsif)."
            : `Menggunakan rata-rata pembacaan terakhir dari ${soilSensors.length} sensor (default). Cocok untuk lahan homogen.`}
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Rentang kelembapan tanah</Label>
          <div className="text-sm font-mono">{range[0]}% – {range[1]}%</div>
        </div>
        <Slider
          value={range}
          onValueChange={setRange}
          min={0}
          max={100}
          step={1}
          minStepsBetweenThumbs={5}
        />
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="space-y-1">
            <Label className="text-xs">Minimum (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={range[0]}
              onChange={(e) => setRange([Number(e.target.value), range[1]])}
              data-testid={CONTROL.autoSoilMin}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Maksimum (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={range[1]}
              onChange={(e) => setRange([range[0], Number(e.target.value)])}
              data-testid={CONTROL.autoSoilMax}
            />
          </div>
        </div>
      </div>
      <Button
        onClick={() =>
          onUpdate({
            mode: "auto",
            auto_soil_min_pct: range[0],
            auto_soil_max_pct: range[1],
            auto_sensor_id: refSensorId || null,
          })
        }
        disabled={disabled}
        data-testid="auto-save-btn"
      >
        <Save className="w-4 h-4 mr-1" /> Simpan ambang
      </Button>
      <p className="text-[11px] text-muted-foreground">
        Backend cek soil moisture setiap 5 detik. Valve ON jika di bawah MIN, OFF jika di atas MAX.
      </p>
    </div>
  );
}

export default function Control() {
  const { currentId, current } = useProject();
  const { user } = useAuth();
  const [solenoids, setSolenoids] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [batchSelection, setBatchSelection] = useState(new Set()); // multi-select for batch ON/OFF
  const canEdit = user?.role === "admin" || user?.role === "project_admin";

  const fetchAll = async () => {
    if (!currentId) return;
    const [sRes, sensorsRes] = await Promise.all([
      api.get(`/projects/${currentId}/solenoids`),
      api.get(`/projects/${currentId}/sensors`),
    ]);
    setSolenoids(sRes.data);
    setSensors(sensorsRes.data);
    // Use functional setState to avoid stale-closure resetting selection back to the first
    // valve every 4s (setInterval captures the initial `selectedId=null` closure).
    if (sRes.data.length) {
      setSelectedId((prev) => {
        if (prev && sRes.data.find((s) => s.id === prev)) return prev;
        return sRes.data[0].id;
      });
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const cfg = solenoids.find((s) => s.id === selectedId);
  const sensor = cfg ? sensors.find((s) => s.id === cfg.sensor_id) : null;

  // Ping the sketch generator so `project_code` reflects the latest sensor/config state.
  // Fire-and-forget — the frontend doesn't need to wait, and errors here are non-fatal.
  const regenerateSketch = async () => {
    try { await api.get(`/projects/${currentId}/esp32-sketch`); } catch (_) { /* non-fatal */ }
  };

  const update = async (payload) => {
    if (!cfg) return;
    // If user has ≥2 valves batch-selected AND the currently-viewed cfg is one of them,
    // toggling `is_on` should apply to ALL selected valves (user's mental model when
    // clicking Manual switch after selecting multiple valves via checkbox).
    // Mode/schedule/threshold changes stay per-valve to avoid unintended overwrites.
    const isBatchToggle =
      "is_on" in payload &&
      batchSelection.has(cfg.id) &&
      batchSelection.size >= 2;
    if (isBatchToggle) {
      let ok = 0, fail = 0;
      for (const id of batchSelection) {
        try {
          await api.patch(`/solenoids/${id}`, { mode: "manual", ...payload });
          ok++;
        } catch { fail++; }
      }
      toast.success(`${ok} valve ${payload.is_on ? "ON" : "OFF"}${fail ? ` · ${fail} gagal` : ""} · sketch di-regenerate`);
      regenerateSketch();
      await fetchAll();
      return;
    }
    try {
      const { data } = await api.patch(`/solenoids/${cfg.id}`, payload);
      setSolenoids((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      toast.success("Konfigurasi diperbarui · sketch di-regenerate");
      regenerateSketch();
    } catch (e) {
      toast.error("Gagal memperbarui");
    }
  };

  const changeMode = (mode) => update({ mode });

  // ---- Delete valve (admin & project_admin only) ----
  const deleteValve = async (solenoidCfg) => {
    if (!canEdit) return;
    const sn = sensors.find((x) => x.id === solenoidCfg.sensor_id);
    const label = sn?.name || solenoidCfg.sensor_id.slice(0, 8);
    if (!window.confirm(`Hapus valve "${label}"? Sensor akan dihapus permanen dan sketch akan diregenerate.`)) return;
    try {
      // Deleting the sensor cascades to solenoid_configs (backend hook).
      await api.delete(`/sensors/${solenoidCfg.sensor_id}`);
      toast.success(`Valve "${label}" dihapus · sketch di-regenerate`);
      setBatchSelection((prev) => { const n = new Set(prev); n.delete(solenoidCfg.id); return n; });
      if (selectedId === solenoidCfg.id) setSelectedId(null);
      regenerateSketch();
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus valve");
    }
  };

  // ---- Batch ON/OFF multiple valves at once ----
  const toggleBatchPick = (id) => {
    setBatchSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyBatch = async (turnOn) => {
    if (batchSelection.size === 0) {
      toast.info("Pilih dulu valve yang ingin diubah (ceklis kotak di setiap valve)");
      return;
    }
    let ok = 0, fail = 0;
    for (const id of batchSelection) {
      try {
        // Switching mode to "manual" ensures the on/off write is respected
        await api.patch(`/solenoids/${id}`, { mode: "manual", is_on: turnOn });
        ok++;
      } catch { fail++; }
    }
    toast.success(`${ok} valve ${turnOn ? "ON" : "OFF"}${fail ? ` · ${fail} gagal` : ""} · sketch di-regenerate`);
    setBatchSelection(new Set());
    regenerateSketch();
    await fetchAll();
  };

  if (!currentId) {
    return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;
  }
  if (!cfg) {
    return (
      <div>
        <PageHeader title="Kontrol Solenoid" overline={current?.name} description="Mode Manual / Scheduling / Otomatis." />
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          Belum ada solenoid pada proyek ini. Tambahkan di menu Sensor Manajemen.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Kontrol Solenoid"
        overline={current?.name}
        description="Pilih mode kendali valve: Manual, Scheduling, atau Otomatis berbasis kelembapan tanah."
      />

      <details className="mb-6 border border-primary/30 bg-primary/5 rounded-md" data-testid="control-howto">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center gap-2 select-none">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs">?</span>
          Cara pakai Kontrol Solenoid
          <span className="ml-auto text-xs text-muted-foreground">klik untuk buka</span>
        </summary>
        <div className="px-4 pb-4 pt-1 text-xs text-muted-foreground space-y-3">
          <div>
            <strong className="text-foreground">Pilih valve dulu</strong>: klik salah satu di kartu &ldquo;Valve Solenoid&rdquo; di bawah untuk mengaktifkan tab kontrol. Anda bisa centang checkbox untuk aksi batch (Aktifkan Semua / Matikan Semua). Admin &amp; Admin Anggota bisa hapus valve via icon 🗑️.
          </div>
          <div>
            <div className="font-medium text-foreground mb-1">1. Manual</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Toggle switch <strong>ON/OFF</strong> — relay langsung switch dalam ≤2 detik (ESP32 poll state tiap 2 detik).</li>
              <li>Cocok untuk kontrol langsung, siram cepat, atau test hardware.</li>
              <li>Tidak perlu re-upload sketch — cukup ESP32 tetap terkoneksi Wi-Fi.</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-foreground mb-1">2. Scheduling</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Set <strong>Jam ON</strong> (contoh: 06:00) dan <strong>Jam OFF</strong> (contoh: 06:30) untuk penyiraman pagi.</li>
              <li>Pilih hari aktif (Senin–Minggu).</li>
              <li>Backend evaluate tiap menit; ESP32 apply state via polling. Jam mengikuti timezone server (WIB/UTC+7).</li>
              <li>Cocok untuk irigasi rutin harian tanpa perlu intervensi manual.</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-foreground mb-1">3. Otomatis (berbasis kelembapan)</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Set <strong>Soil MIN %</strong> (contoh: 30%) — valve otomatis <strong>ON</strong> saat kelembapan turun di bawah nilai ini.</li>
              <li>Set <strong>Soil MAX %</strong> (contoh: 70%) — valve otomatis <strong>OFF</strong> saat kelembapan sudah di atas nilai ini.</li>
              <li>Pilih sensor referensi (soil_moisture) di dropdown. Backend cek reading terbaru; ESP32 apply via polling.</li>
              <li>Cocok untuk mempertahankan kelembapan optimal tanpa boros air.</li>
            </ul>
          </div>
          <div className="border-t border-border pt-2 mt-2">
            <div className="font-medium text-foreground mb-1">Catatan penting</div>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><strong>Ubah mode/jadwal/threshold</strong> → TIDAK perlu re-upload sketch. ESP32 poll setiap 2 detik.</li>
              <li><strong>Tambah/hapus valve solenoid</strong> → PERLU re-upload sketch (array pin di-hardcode saat compile). Klik notifikasi &ldquo;sketch regenerated&rdquo; atau tab ESP32 Wizard → Sketch → Download → upload ulang.</li>
              <li>Cek status ESP32 di dashboard: badge <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 text-[10px] font-medium">LIVE</span> = ESP32 push data ≤30s, <span className="inline-block px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 text-[10px] font-medium">OFFLINE</span> = kontrol tidak sampai ke relay.</li>
            </ul>
          </div>
        </div>
      </details>

      <div className="border border-border rounded-md bg-card p-4 mb-6" data-testid="valve-picker-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="overline text-muted-foreground">Valve Solenoid ({solenoids.length})</div>
          {batchSelection.size > 0 && (
            <div className="flex items-center gap-2" data-testid="valve-batch-actions">
              <span className="text-xs text-muted-foreground">{batchSelection.size} terpilih</span>
              <button
                type="button"
                onClick={() => applyBatch(true)}
                className="px-2.5 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:opacity-90"
                data-testid="valve-batch-on"
              >
                Aktifkan Semua
              </button>
              <button
                type="button"
                onClick={() => applyBatch(false)}
                className="px-2.5 py-1 rounded bg-secondary text-secondary-foreground text-xs font-medium hover:opacity-90"
                data-testid="valve-batch-off"
              >
                Matikan Semua
              </button>
              <button
                type="button"
                onClick={() => setBatchSelection(new Set())}
                className="text-xs text-muted-foreground underline"
              >
                Batal pilih
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {solenoids.map((s) => {
            const sn = sensors.find((x) => x.id === s.sensor_id);
            const isActive = s.id === selectedId;
            const isBatched = batchSelection.has(s.id);
            return (
              <div
                key={s.id}
                className={`flex items-center gap-1.5 rounded-md border transition-colors duration-200 ${
                  isActive ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                } ${isBatched ? "ring-2 ring-primary/40" : ""}`}
                data-testid={`valve-item-${s.id}`}
              >
                {/* Batch select checkbox */}
                <label className="pl-2 pr-1 py-1.5 cursor-pointer" title="Pilih untuk aksi batch">
                  <input
                    type="checkbox"
                    checked={isBatched}
                    onChange={() => toggleBatchPick(s.id)}
                    className="h-3.5 w-3.5 accent-primary rounded"
                    data-testid={`valve-batch-check-${s.id}`}
                  />
                </label>
                {/* Main click area to open config */}
                <button
                  type="button"
                  onClick={() => setSelectedId(s.id)}
                  data-testid={`valve-pick-${s.id}`}
                  className="flex items-center gap-2 py-1.5 pr-2 text-sm"
                >
                  {s.is_on ? <ToggleRight className="w-4 h-4 text-primary" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                  <span className={isActive ? "text-foreground" : "text-muted-foreground"}>
                    {sn?.name || s.sensor_id.slice(0, 6)}
                  </span>
                  <span className={`text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded ${s.is_on ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                    {s.is_on ? "ON" : "OFF"}
                  </span>
                </button>
                {/* Delete (admin & project_admin only) */}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => deleteValve(s)}
                    className="pr-2 py-1.5 text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`valve-delete-${s.id}`}
                    title="Hapus valve"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-muted-foreground">Sensor</div>
            <h3 className="font-display text-lg font-semibold mt-1">{sensor?.name}</h3>
          </div>
          <div className="text-xs text-muted-foreground">Mode aktif: <span className="font-medium text-foreground uppercase">{cfg.mode}</span></div>
        </div>

        <Tabs value={cfg.mode} onValueChange={changeMode} data-testid={CONTROL.tabsRoot}>
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="manual" data-testid={CONTROL.tabManual}>Manual</TabsTrigger>
            <TabsTrigger value="schedule" data-testid={CONTROL.tabSchedule}>Scheduling</TabsTrigger>
            <TabsTrigger value="auto" data-testid={CONTROL.tabAuto}>Otomatis</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-6">
            <ManualPanel cfg={cfg} onUpdate={update} disabled={user.role !== "admin" && false /* allow all viewers? keep editable */} batchCount={batchSelection.has(cfg.id) ? batchSelection.size : 0} />
          </TabsContent>
          <TabsContent value="schedule" className="mt-6">
            <SchedulePanel cfg={cfg} onUpdate={update} disabled={false} />
          </TabsContent>
          <TabsContent value="auto" className="mt-6">
            <AutoPanel
              cfg={cfg}
              onUpdate={update}
              disabled={false}
              soilSensors={sensors.filter((s) => s.type === "soil_moisture")}
              projectId={currentId}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
